import * as THREE from 'three';

interface FigureUniforms extends Record<string, THREE.IUniform> {
    time: THREE.IUniform<number>;
    baseColor: THREE.IUniform<THREE.Color>;
    viewPosition: THREE.IUniform<THREE.Vector3>;
    audioIntensity: THREE.IUniform<number>;
}

interface AuraUniforms extends Record<string, THREE.IUniform> {
    time: THREE.IUniform<number>;
    viewPosition: THREE.IUniform<THREE.Vector3>;
    audioIntensity: THREE.IUniform<number>;
}

export class GlowingFigure {
    public mesh!: THREE.Mesh<THREE.IcosahedronGeometry, THREE.ShaderMaterial>;
    private aura!: THREE.Mesh<THREE.SphereGeometry, THREE.ShaderMaterial>;
    private mainLight!: THREE.PointLight;
    private ambientLight!: THREE.PointLight;
    private fillLight!: THREE.PointLight;
    public basePosition: THREE.Vector3;

    constructor() {
        this.basePosition = new THREE.Vector3(0, 2, -50);
        this.createFigure();
        this.createLights();
        this.createAura();
    }

    private createFigure(): void {
        // Create a more complex geometry for the core
        const geometry = new THREE.IcosahedronGeometry(0.8, 2);
        
        // Custom shader material for the figure
        const material = new THREE.ShaderMaterial({
            uniforms: {
                time: { value: 0 },
                baseColor: { value: new THREE.Color(0xff1111) },
                viewPosition: { value: new THREE.Vector3() },
                audioIntensity: { value: 0 }
            } as FigureUniforms,
            vertexShader: `
                varying vec3 vNormal;
                varying vec3 vPosition;
                varying vec3 vViewPosition;
                uniform vec3 viewPosition;
                
                void main() {
                    vNormal = normalize(normalMatrix * normal);
                    vPosition = position;
                    vec4 worldPosition = modelMatrix * vec4(position, 1.0);
                    vViewPosition = viewPosition - worldPosition.xyz;
                    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
                }
            `,
            fragmentShader: `
                uniform float time;
                uniform vec3 baseColor;
                uniform float audioIntensity;
                varying vec3 vNormal;
                varying vec3 vPosition;
                varying vec3 vViewPosition;
                
                void main() {
                    float pulse = sin(time * 0.001) * 0.5 + 0.5;
                    
                    // Enhanced fresnel effect based on view distance
                    float viewDist = length(vViewPosition);
                    float distanceFactor = smoothstep(20.0, 5.0, viewDist); // Increase effect when closer
                    
                    float fresnel = pow(1.0 - abs(dot(normalize(vViewPosition), vNormal)), 3.0);
                    fresnel = mix(fresnel, fresnel * 2.0, distanceFactor); // Enhance fresnel when closer
                    
                    float audioBoost = audioIntensity * 0.5;
                    float intensity = mix(0.8, 1.2, (pulse + audioBoost) * distanceFactor); // Increase intensity when closer
                    vec3 glowColor = baseColor * (2.5 + distanceFactor + audioBoost); // Brighter glow when closer
                    
                    vec3 color = mix(baseColor, glowColor, fresnel) * intensity;
                    
                    // Add subtle pulsing rim light when close
                    float rimPulse = sin(time * 0.002 + length(vPosition) * 2.0) * 0.5 + 0.5;
                    color += glowColor * fresnel * rimPulse * distanceFactor * 0.5;
                    
                    gl_FragColor = vec4(color, 0.8 + fresnel * 0.4);
                }
            `,
            transparent: true,
            depthWrite: false
        });

        this.mesh = new THREE.Mesh(geometry, material);
        this.mesh.position.copy(this.basePosition);
    }

    private createLights(): void {
        // Primary glow light - stronger and more focused
        this.mainLight = new THREE.PointLight(0xff1111, 3.0, 20);
        this.mainLight.decay = 1.5;
        this.mainLight.position.copy(this.basePosition);
        
        // Wider, softer light for atmosphere
        this.ambientLight = new THREE.PointLight(0xff2222, 0.8, 40);
        this.ambientLight.decay = 2.0;
        this.ambientLight.position.copy(this.basePosition);
        
        // Add a third light for better coverage
        this.fillLight = new THREE.PointLight(0xff1111, 0.5, 30);
        this.fillLight.decay = 2.0;
        this.fillLight.position.copy(this.basePosition);
    }

    private createAura(): void {
        // Create outer aura
        const auraGeometry = new THREE.SphereGeometry(1.2, 32, 32);
        const auraMaterial = new THREE.ShaderMaterial({
            uniforms: {
                time: { value: 0 },
                viewPosition: { value: new THREE.Vector3() },
                audioIntensity: { value: 0 }
            } as AuraUniforms,
            vertexShader: `
                varying vec3 vNormal;
                varying vec3 vViewPosition;
                uniform vec3 viewPosition;
                
                void main() {
                    vNormal = normalize(normalMatrix * normal);
                    vec4 worldPosition = modelMatrix * vec4(position, 1.0);
                    vViewPosition = viewPosition - worldPosition.xyz;
                    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
                }
            `,
            fragmentShader: `
                uniform float time;
                uniform float audioIntensity;
                varying vec3 vNormal;
                varying vec3 vViewPosition;
                
                void main() {
                    float viewDist = length(vViewPosition);
                    float distanceFactor = smoothstep(20.0, 5.0, viewDist);
                    
                    float pulse = sin(time * 0.0005) * 0.5 + 0.5;
                    float fresnel = pow(1.0 - abs(dot(normalize(vViewPosition), vNormal)), 4.0);
                    
                    // Enhanced intensity when closer
                    float audioBoost = audioIntensity * 0.8;
                    float intensity = mix(1.0, 1.5, (pulse + audioBoost) * distanceFactor);
                    
                    // Adjust colors based on distance
                    vec3 innerColor = mix(vec3(1.0, 0.1, 0.1), vec3(1.0, 0.2, 0.2), distanceFactor);
                    vec3 outerColor = mix(vec3(1.0, 0.3, 0.3), vec3(1.0, 0.4, 0.4), distanceFactor);
                    
                    vec3 color = mix(innerColor, outerColor, fresnel) * fresnel * intensity;
                    
                    // Add subtle energy waves when close
                    float energyWaves = sin(time * 0.001 + fresnel * 10.0) * 0.5 + 0.5;
                    color += outerColor * fresnel * energyWaves * distanceFactor * 0.3;
                    
                    gl_FragColor = vec4(color, fresnel * mix(0.4, 0.6, distanceFactor));
                }
            `,
            transparent: true,
            depthWrite: false,
            blending: THREE.AdditiveBlending
        });

        this.aura = new THREE.Mesh(auraGeometry, auraMaterial);
        this.aura.position.copy(this.basePosition);
    }

    update(time: number): void {
        // Update shader uniforms
        if (this.mesh?.material?.uniforms) {
            this.mesh.material.uniforms.time.value = time;
            if (this.mesh.material.uniforms.audioIntensity?.value !== undefined) {
                const audioValue = this.mesh.material.uniforms.audioIntensity.value;
                this.mainLight.intensity = 3.0 + audioValue * 2.0;
                this.ambientLight.intensity = 0.8 + audioValue * 0.5;
                this.fillLight.intensity = 0.5 + audioValue * 0.3;
            }
        }
        if (this.aura?.material?.uniforms) {
            this.aura.material.uniforms.time.value = time;
            if (this.mesh.material.uniforms.audioIntensity?.value !== undefined) {
                this.aura.material.uniforms.audioIntensity.value = this.mesh.material.uniforms.audioIntensity.value;
            }
        }
        
        // Subtle movement
        const slowTime = time * 0.0003;
        this.mesh.rotation.y = slowTime;
        this.aura.rotation.y = -slowTime * 0.5;
        
        // Enhanced breathing effect
        const breathe = Math.sin(time * 0.001) * 0.15;
        this.aura.scale.setScalar(1 + breathe);
        
        // Keep all components aligned
        this.mainLight.position.copy(this.basePosition);
        this.ambientLight.position.copy(this.basePosition);
        this.fillLight.position.copy(this.basePosition);
        this.aura.position.copy(this.basePosition);
        
        // Complex light pulsing
        const basePulse = Math.sin(time * 0.001);
        const detailPulse = Math.sin(time * 0.002) * 0.3;
        const fastPulse = Math.sin(time * 0.003) * 0.2;
        
        // Main light pulses more dramatically
        this.mainLight.intensity = 3.0 + basePulse * 1.0 + detailPulse;
        // Ambient light stays more stable
        this.ambientLight.intensity = 0.8 + basePulse * 0.2;
        // Fill light adds subtle variation
        this.fillLight.intensity = 0.5 + fastPulse;
    }

    updateViewPosition(camera: THREE.Camera): void {
        if (this.mesh?.material?.uniforms) {
            this.mesh.material.uniforms.viewPosition.value.copy(camera.position);
        }
        if (this.aura?.material?.uniforms) {
            this.aura.material.uniforms.viewPosition.value.copy(camera.position);
        }
    }

    addToScene(scene: THREE.Scene): void {
        scene.add(this.mesh);
        scene.add(this.aura);
        scene.add(this.mainLight);
        scene.add(this.ambientLight);
        scene.add(this.fillLight);
    }

    dispose(): void {
        // Cleanup geometries
        this.mesh?.geometry?.dispose();
        this.aura?.geometry?.dispose();
        
        // Cleanup materials
        this.mesh?.material?.dispose();
        this.aura?.material?.dispose();
        
        // Remove from scene
        this.mesh?.parent?.remove(this.mesh);
        this.aura?.parent?.remove(this.aura);
        this.mainLight?.parent?.remove(this.mainLight);
        this.ambientLight?.parent?.remove(this.ambientLight);
        
        // Cleanup uniforms/textures
        [this.mesh?.material, this.aura?.material].forEach(material => {
            if (material?.uniforms) {
                Object.values(material.uniforms)
                    .forEach(uniform => {
                        if (uniform.value instanceof THREE.Texture) {
                            uniform.value.dispose();
                        }
                    });
            }
        });
    }
}
